import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Truck, MapPin, Shield, Phone, Mail, Loader2, Filter, ChevronRight } from "lucide-react";
import { api } from "@/api/client";
import RouteCalculator from "@/components/RouteCalculator";
import { logger } from "@/utils/logger";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

const SPECIES_OPTIONS = [
  { value: "", label: "All Species" },
  { value: "equine", label: "Horses & Equine" },
  { value: "canine", label: "Dogs & Canine" },
  { value: "feline", label: "Cats & Feline" },
  { value: "cattle", label: "Cattle & Livestock" },
  { value: "exotics", label: "Exotics" },
  { value: "others", label: "Farm Animals" }
];

const BrowseTransporters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [transporters, setTransporters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  
  const [filters, setFilters] = useState({
    species: searchParams.get("species") || "",
    origin_state: searchParams.get("origin") || "",
    destination_state: searchParams.get("destination") || ""
  });

  useEffect(() => {
    fetchTransporters();
  }, [filters]);

  const fetchTransporters = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.species) params.append("species", filters.species);
      if (filters.origin_state) params.append("origin_state", filters.origin_state);
      if (filters.destination_state) params.append("destination_state", filters.destination_state);
      
      const response = await api.get(`/transporters/browse?${params.toString()}`);
      setTransporters(response.data.transporters || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      logger.error("Failed to fetch transporters:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key === "origin_state" ? "origin" : key === "destination_state" ? "destination" : key, value);
    } else {
      newParams.delete(key === "origin_state" ? "origin" : key === "destination_state" ? "destination" : key);
    }
    setSearchParams(newParams);
  };

  const TransporterCard = ({ transporter }) => (
    <Link
      to={`/transporters/${transporter.transporter_id}`}
      className="glass-card p-6 hover:border-[#3B82F6]/50 transition-colors block"
      data-testid={`transporter-card-${transporter.transporter_id}`}
    >
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-full bg-[#3B82F6]/10 flex items-center justify-center flex-shrink-0">
          <Truck className="w-7 h-7 text-[#3B82F6]" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg text-[#EDEDED] truncate">{transporter.business_name}</h3>
            {transporter.is_verified && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-[#22C55E]/10 text-[#22C55E] rounded text-xs">
                <Shield className="w-3 h-3" /> Verified
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2 text-sm text-[#A1A1AA] mb-3">
            <MapPin className="w-4 h-4" />
            <span>
              {transporter.location_city && transporter.location_state 
                ? `${transporter.location_city}, ${transporter.location_state}`
                : "Location not specified"}
            </span>
            {transporter.years_experience && (
              <span className="text-[#71717A]">• {transporter.years_experience} yrs exp</span>
            )}
          </div>

          {/* Species */}
          <div className="flex flex-wrap gap-1 mb-3">
            {transporter.species_served?.slice(0, 4).map(species => (
              <span key={species} className="px-2 py-0.5 bg-[#27272A] text-[#A1A1AA] rounded text-xs capitalize">
                {species}
              </span>
            ))}
            {transporter.species_served?.length > 4 && (
              <span className="px-2 py-0.5 bg-[#27272A] text-[#71717A] rounded text-xs">
                +{transporter.species_served.length - 4} more
              </span>
            )}
          </div>

          {/* Service Areas */}
          <div className="flex flex-wrap gap-1 mb-3">
            {transporter.regions_served?.slice(0, 8).map(state => (
              <span key={state} className="px-1.5 py-0.5 bg-[#3B82F6]/10 text-[#3B82F6] rounded text-xs font-medium">
                {state}
              </span>
            ))}
            {transporter.regions_served?.length > 8 && (
              <span className="px-1.5 py-0.5 text-[#71717A] text-xs">
                +{transporter.regions_served.length - 8} states
              </span>
            )}
          </div>

          {/* Pricing */}
          <div className="flex items-center gap-4 text-sm">
            {transporter.base_rate_per_mile && (
              <span className="text-[#EDEDED]">
                <span className="text-[#71717A]">Rate:</span> ${transporter.base_rate_per_mile.toFixed(2)}/mi
              </span>
            )}
            {transporter.minimum_charge && (
              <span className="text-[#EDEDED]">
                <span className="text-[#71717A]">Min:</span> ${transporter.minimum_charge.toFixed(0)}
              </span>
            )}
          </div>
        </div>

        <ChevronRight className="w-5 h-5 text-[#3F3F46]" />
      </div>
    </Link>
  );

  return (
    <div className="pt-20 pb-16 px-6 md:px-12 min-h-screen bg-[#050505]">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#3B82F6]/10 text-[#3B82F6] rounded-full text-sm mb-4">
            <Truck className="w-4 h-4" /> Transporter Registry
          </div>
          <h1 className="text-3xl md:text-4xl text-[#EDEDED] mb-3">Find Animal Transporters</h1>
          <p className="text-[#A1A1AA] max-w-xl mx-auto">
            Connect with verified transporters for safe, professional animal transport services
          </p>
        </div>

        {/* Route Calculator */}
        <div className="mb-8">
          <RouteCalculator />
        </div>

        {/* Filters */}
        <div className="glass-card p-4 mb-6">
          <div className="flex items-center justify-between mb-4 md:hidden">
            <span className="text-[#A1A1AA] text-sm">Filters</span>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-1 text-[#3B82F6] text-sm"
            >
              <Filter className="w-4 h-4" />
              {showFilters ? "Hide" : "Show"}
            </button>
          </div>
          
          <div className={`grid md:grid-cols-3 gap-4 ${showFilters ? "block" : "hidden md:grid"}`}>
            <div>
              <label className="block text-xs text-[#71717A] mb-1">Species</label>
              <select
                value={filters.species}
                onChange={(e) => handleFilterChange("species", e.target.value)}
                className="input-minimal w-full"
                data-testid="species-filter"
              >
                {SPECIES_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-xs text-[#71717A] mb-1">Pickup State</label>
              <select
                value={filters.origin_state}
                onChange={(e) => handleFilterChange("origin_state", e.target.value)}
                className="input-minimal w-full"
                data-testid="origin-filter"
              >
                <option value="">Any State</option>
                {US_STATES.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-xs text-[#71717A] mb-1">Destination State</label>
              <select
                value={filters.destination_state}
                onChange={(e) => handleFilterChange("destination_state", e.target.value)}
                className="input-minimal w-full"
                data-testid="destination-filter"
              >
                <option value="">Any State</option>
                {US_STATES.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="mb-4">
          <p className="text-sm text-[#71717A]">
            {loading ? "Searching..." : `${total} transporter${total !== 1 ? "s" : ""} found`}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#3B82F6] animate-spin" />
          </div>
        ) : transporters.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#3B82F6]/10 flex items-center justify-center">
              <Truck className="w-10 h-10 text-[#3B82F6]" />
            </div>
            <h3 className="text-xl text-[#EDEDED] mb-2">No Transporters Found</h3>
            <p className="text-[#A1A1AA] mb-6">
              {filters.species || filters.origin_state || filters.destination_state
                ? "Try adjusting your filters"
                : "Be the first to register as a transporter!"}
            </p>
            <Link to="/transporter/register" className="btn-gold inline-flex items-center gap-2">
              <Truck className="w-4 h-4" /> Become a Transporter
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {transporters.map(transporter => (
              <TransporterCard key={transporter.transporter_id} transporter={transporter} />
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 glass-card p-8 text-center border-[#3B82F6]/20">
          <h3 className="text-xl text-[#EDEDED] mb-2">Are you a transporter?</h3>
          <p className="text-[#A1A1AA] mb-6">
            Join Breedling's network of verified animal transporters and connect with customers
          </p>
          <Link to="/transporter/register" className="btn-gold inline-flex items-center gap-2">
            <Truck className="w-4 h-4" /> Register as a Transporter
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BrowseTransporters;
